import type { PanoramaHttpApi } from 'api-validations/http'
import { toFormData, toURLSearchParams } from 'utils/object'

import { ApiError } from '../ApiError'
import type { PublicApi } from '../types'

const checkStatus = async (response: Response, throwIfNotOk = true) => {
  if (throwIfNotOk && !response.ok) {
    throw new ApiError(response, await response.text())
  }
  return response
}

const parse = async (response: Response) => {
  // essence is the type and subtype of the content type, per the spec (https://mimesniff.spec.whatwg.org/#mime-type-essence)
  const essence = response.headers.get('content-type')?.trim()?.toLowerCase()?.split(';')[0]

  return {
    body:
      essence === 'application/json' || essence === 'application/hal+json'
        ? ((await response.json()) as unknown)
        : essence === 'text/plain'
          ? await response.text()
          : undefined,
    status: response.status,
    headers: response.headers,
    /**
     * Returns a promise that resolves with the response body as a Blob.
     * If the content type is json or text, the body has already been consumed, and this will throw an error.
     */
    blob() {
      return response.blob()
    },
    /**
     * Returns a promise that resolves with the response body as a FormData object.
     * If the content type is json or text, the body has already been consumed, and this will throw an error.
     */
    arrayBuffer() {
      return response.arrayBuffer()
    }
  }
}

/**
 * A simple API client that handles appending the locale to the URL and adding the Authorization header.
 *
 * It also parses the response body based on the content type: application/json, text/plain, no content, etc.
 *
 * If the content type is text or json, it will consume the body and parse appropriately.
 * Otherwise, you can call the blob() or arrayBuffer() methods to consume the body of binary data.
 *
 * Throws an ApiError if response is not ok. This behavior can be overridden by passing throwIfNotOk: false in the options.
 */
export class ApiClient {
  private apiBaseUrl: string
  private pathPrefix: string
  private tokenService
  private credentials
  private locale
  private fetch

  constructor(options: ApiClientOptions) {
    this.apiBaseUrl = options.apiBaseUrl
    this.pathPrefix = options.pathPrefix ?? ''
    this.tokenService = options.tokenService
    this.credentials = options.credentials ?? 'include'
    this.locale = options.locale ?? 'en-US'
    const fetcher = options.fetch ?? fetch

    this.fetch = async (path: string, init: RequestInit, throwIfNotOkay: boolean) => {
      const url = new URL(this.pathPrefix + path, this.apiBaseUrl)
      url.searchParams.append('l', this.locale)

      return fetcher(url.toString(), init)
        .then((res) => checkStatus(res, throwIfNotOkay))
        .then((res) => parse(res))
    }
  }

  private async initHeaders() {
    const headers = new Headers()

    if (!this.tokenService) {
      return headers
    }

    headers.set('Authorization', `Bearer ${await this.tokenService.getAccessToken()}`)

    return headers
  }

  /**
   * Serializes the body based on the content type, and sets the Content-Type header.
   */
  private serializeBody(body: unknown, headers: Headers) {
    if (
      body === undefined ||
      body instanceof FormData ||
      body instanceof URLSearchParams ||
      body instanceof ReadableStream ||
      body instanceof Blob
    ) {
      return body
    }

    headers.set('Content-Type', 'application/json')

    return JSON.stringify(body)
  }

  async get(
    endpoint: string,
    { throwIfNotOk = true, includeCredentials = true }: RequestOptions = {}
  ) {
    const options = includeCredentials
      ? {
          method: 'GET',
          headers: await this.initHeaders(),
          credentials: this.credentials
        }
      : { method: 'GET' }

    return this.fetch(endpoint, options, throwIfNotOk)
  }

  async post(endpoint: string, { body, throwIfNotOk = true }: RequestOptions = {}) {
    const headers = await this.initHeaders()

    const options: RequestInit = {
      method: 'POST',
      body: this.serializeBody(body, headers),
      headers,
      credentials: this.credentials
    }

    return this.fetch(endpoint, options, throwIfNotOk)
  }

  async put(endpoint: string, { body, throwIfNotOk = true }: RequestOptions = {}) {
    const headers = await this.initHeaders()

    const options: RequestInit = {
      method: 'PUT',
      body: this.serializeBody(body, headers),
      headers,
      credentials: this.credentials
    }
    return this.fetch(endpoint, options, throwIfNotOk)
  }

  async delete(endpoint: string) {
    const options: RequestInit = {
      method: 'DELETE',
      headers: await this.initHeaders(),
      credentials: this.credentials
    }
    const response = await this.fetch(endpoint, options, true)

    return { status: response.status }
  }

  async callApi<ApiDef extends PanoramaHttpApi.AnyApiDefinition>(
    definition: ApiDef,
    params: PanoramaHttpApi.PathParams<ApiDef>,
    body: PanoramaHttpApi.RawRequestBody<ApiDef>,
    options?: CallApiOptions
  ): Promise<PanoramaHttpApi.ParsedResponse<ApiDef>>
  async callApi<ApiDef extends PanoramaHttpApi.VoidBodyApiDefinition>(
    definition: ApiDef,
    params: PanoramaHttpApi.PathParams<ApiDef>,
    options?: CallApiOptions
  ): Promise<PanoramaHttpApi.ParsedResponse<ApiDef>>
  async callApi<ApiDef extends PanoramaHttpApi.AnyApiDefinition>(
    definition: ApiDef,
    params: NoInfer<PanoramaHttpApi.PathParams<ApiDef>>,
    rawBody?: NoInfer<PanoramaHttpApi.RawRequestBody<ApiDef>>,
    callOptions?: CallApiOptions
  ) {
    try {
      const { method = 'GET', requestBodySchema } = definition

      const path = definition.pathFor(params)

      const headers = await this.initHeaders()

      if (callOptions?.extraHeaders !== undefined) {
        Object.entries(callOptions.extraHeaders).forEach(([key, value]) => {
          headers.set(key, value)
        })
      }

      let body: any = rawBody

      body =
        body && requestBodySchema
          ? definition.supportsCodec === true
            ? requestBodySchema.encode(body)
            : requestBodySchema.parse(body)
          : undefined

      if (definition.requestBodyEncoding === 'form-data' && body) {
        body = toFormData(body)
      }

      if (definition.requestBodyEncoding === 'url-encoded' && body) {
        headers.set('Content-Type', 'application/x-www-form-urlencoded')
        body = toURLSearchParams(body)
      }

      const response = await this.fetch(
        path,
        {
          method,
          body: this.serializeBody(body, headers),
          headers,
          credentials: this.credentials
        },
        true
      )

      return definition.responseSchema.parse(
        response.body
      ) as PanoramaHttpApi.ParsedResponse<ApiDef>
    } catch (error) {
      if (definition.errorMap) {
        const mappedError = await definition.errorMap(error as ErrorLike)

        throw mappedError
      }
      throw error
    }
  }

  async callInternalApi<ApiDef extends PanoramaHttpApi.VoidBodyApiDefinition>(
    definition: ApiDef,
    params: PanoramaHttpApi.PathParams<ApiDef>,
    options?: InternalApiOptions
  ): Promise<PanoramaHttpApi.ParsedResponse<ApiDef>>
  async callInternalApi<ApiDef extends PanoramaHttpApi.AnyApiDefinition>(
    definition: ApiDef,
    params: PanoramaHttpApi.PathParams<ApiDef>,
    body: PanoramaHttpApi.RawRequestBody<ApiDef>,
    options?: InternalApiOptions
  ): Promise<PanoramaHttpApi.ParsedResponse<ApiDef>>
  async callInternalApi<ApiDef extends PanoramaHttpApi.AnyApiDefinition>(
    definition: ApiDef,
    params: PanoramaHttpApi.PathParams<ApiDef>,
    rawBody?: PanoramaHttpApi.RawRequestBody<ApiDef>,
    { onBehalfOf }: InternalApiOptions = {}
  ) {
    const internalDefinition = {
      ...definition,
      pathFor: () => `/internal${definition.pathFor(params)}`
    }

    return this.callApi(
      internalDefinition,
      params,
      rawBody as any,
      onBehalfOf === undefined ? undefined : { extraHeaders: { 'x-on-behalf-of': onBehalfOf } }
    )
  }

  async getAccessToken() {
    if (!this.tokenService) {
      throw new Error('Token service not provided')
    }

    return this.tokenService.getAccessToken()
  }
}

export type TokenService = {
  getAccessToken: () => Promise<string>
}

type FetchFunction = (url: string, init?: RequestInit) => Promise<Response>

type ApiClientOptions = {
  apiBaseUrl: string
  pathPrefix?: string
  tokenService?: TokenService
  credentials?: RequestInit['credentials']
  locale?: string
  fetch?: FetchFunction
}

type RequestOptions =
  | {
      body?: object
      throwIfNotOk?: boolean
      includeCredentials?: boolean
    }
  | undefined

type ErrorLike = PublicApi<Error>

/**
 *  Our router uses :param style path parameters, but API Gateway
 *  uses {param} style. This utility converts from one to the other.
 */
export const toCurlyBraces = (path: string) => path.replace(/:(\w+)/g, '{$1}')

type CallApiOptions = {
  extraHeaders?: Record<string, string>
}

type InternalApiOptions = {
  onBehalfOf?: string
}
